@if (authed() && !hiddenRoute()) {
  <!-- ─────────────── FAB ─────────────── -->
  <button
    type="button"
    class="vd-aib-fab"
    [class.is-open]="open()"
    [attr.aria-label]="(open() ? 'Fermer ' : 'Ouvrir ') + headerTitle"
    [attr.aria-expanded]="open()"
    [title]="headerTitle"
    (click)="toggleOpen()"
  >
    <span class="material-icons-outlined" aria-hidden="true">
      {{ open() ? 'close' : 'auto_awesome' }}
    </span>
    <span class="vd-aib-fab-ring" aria-hidden="true"></span>
  </button>

  <!-- ─────────────── Panel ─────────────── -->
  @if (open()) {
    <section
      class="vd-aib-panel"
      role="dialog"
      aria-modal="false"
      aria-label="Assistant IA"
    >
      <!-- Header -->
      <header class="vd-aib-header">
        <div class="vd-aib-avatar" aria-hidden="true">
          <span class="material-icons-outlined">{{ headerIcon }}</span>
        </div>
        <div class="vd-aib-titles">
          <h2 class="vd-aib-title">{{ headerTitle }}</h2>
          <p class="vd-aib-subtitle">
            <span class="material-icons-outlined" aria-hidden="true">memory</span>
            {{ headerSubtitle }}
          </p>
        </div>
        <div class="vd-aib-actions">
          <button
            type="button"
            class="vd-aib-icon-btn"
            (click)="newConversation()"
            title="Nouvelle conversation"
            aria-label="Nouvelle conversation"
          >
            <span class="material-icons-outlined">add_comment</span>
          </button>
          @if (!isRightHand) {
            <button
              type="button"
              class="vd-aib-icon-btn"
              (click)="openHistory()"
              title="Historique"
              aria-label="Historique des conversations"
            >
              <span class="material-icons-outlined">history</span>
            </button>
          }
          <button
            type="button"
            class="vd-aib-icon-btn"
            (click)="closePanel()"
            title="Fermer"
            aria-label="Fermer"
          >
            <span class="material-icons-outlined">close</span>
          </button>
        </div>
      </header>

      <!-- Body / messages -->
      <div class="vd-aib-body" #messagesScroll (click)="onBodyClick($event)">
        @if (!hasMessages() && !thinking()) {
          <div class="vd-aib-empty">
            <div class="vd-aib-empty-illu" aria-hidden="true">
              <span class="material-icons-outlined">auto_awesome</span>
              <span class="vd-aib-bubble b1"></span>
              <span class="vd-aib-bubble b2"></span>
              <span class="vd-aib-bubble b3"></span>
            </div>
            <h3 class="vd-aib-empty-title">{{ emptyTitle }}</h3>
            <p class="vd-aib-empty-desc">{{ emptyDesc }}</p>
            <div class="vd-aib-chips">
              @for (chip of suggestions; track trackByChip($index, chip)) {
                <button
                  type="button"
                  class="vd-aib-chip"
                  (click)="onPromptSuggestion(chip)"
                  [attr.aria-label]="chip.label"
                >
                  <span class="material-icons-outlined" aria-hidden="true">{{ chip.icon }}</span>
                  {{ chip.label }}
                </button>
              }
            </div>
          </div>
        }

        @if (hasMessages()) {
          <div class="vd-aib-list">
            @for (msg of messages(); track trackByMessage($index, msg)) {
              <article
                class="vd-aib-msg"
                [class.is-user]="msg.role === 'user'"
                [class.is-assistant]="msg.role === 'assistant'"
                [class.has-error]="!!msg.error"
              >
                <!-- Bubble user -->
                @if (msg.role === 'user') {
                  <div class="vd-aib-bubble-user">
                    <span class="vd-aib-msg-text">{{ msg.content?.text || '' }}</span>
                  </div>
                }

                <!-- Bubble assistant -->
                @if (msg.role === 'assistant') {
                  <div class="vd-aib-bubble-assistant">
                    <!-- tool_calls inline -->
                    @if (msg.tool_calls?.length) {
                      <div class="vd-aib-tools">
                        @for (tc of msg.tool_calls || []; track trackByToolCall($index, tc)) {
                          <span
                            class="vd-aib-tool-tag"
                            [class.is-running]="runningToolCall()?.name === tc.name && $last && streaming()"
                          >
                            <span class="material-icons-outlined" aria-hidden="true">build</span>
                            <code>{{ tc.name }}</code>
                            <span class="vd-aib-tool-tag-input">({{ toolInputPreview(tc) }})</span>
                          </span>
                        }
                      </div>
                    }

                    <!-- Texte markdown -->
                    @if (msg.content?.text) {
                      <div class="vd-aib-md" [innerHTML]="renderMd(msg.content?.text)"></div>
                    }

                    <!-- tool_results collapsibles -->
                    @if (msg.tool_results?.length) {
                      <div class="vd-aib-tool-results">
                        @for (tr of msg.tool_results || []; track trackByToolResult($index, tr)) {
                          <details
                            class="vd-aib-tool-result"
                            [open]="isToolResultExpanded(msg.id + '-' + $index)"
                            (toggle)="toggleToolResult(msg.id + '-' + $index)"
                          >
                            <summary>
                              <span class="material-icons-outlined" aria-hidden="true">data_object</span>
                              Résultat <code>{{ tr.name }}</code>
                            </summary>
                            <pre class="vd-aib-tool-pre">{{ toolResultPreview(tr) }}</pre>
                          </details>
                        }
                      </div>
                    }

                    <!-- Footer assistant (latence / erreur) -->
                    @if (msg.error) {
                      <div class="vd-aib-msg-error">
                        <span class="material-icons-outlined" aria-hidden="true">error</span>
                        {{ msg.error }}
                      </div>
                    } @else if (msg.latency_ms != null && !$last) {
                      <span class="vd-aib-msg-latency">{{ msg.latency_ms }} ms</span>
                    }
                  </div>
                }
              </article>
            }

            <!-- Indicator "L'assistant tape..." -->
            @if (thinking() || (streaming() && !messages().length)) {
              <div class="vd-aib-typing" aria-live="polite">
                <span class="vd-aib-dot"></span>
                <span class="vd-aib-dot"></span>
                <span class="vd-aib-dot"></span>
                <span class="vd-aib-typing-label">{{ typingLabel }}</span>
              </div>
            }
          </div>
        }

        <!-- Erreur globale -->
        @if (errorMsg()) {
          <div class="vd-aib-banner-error">
            <span class="material-icons-outlined" aria-hidden="true">error</span>
            <span class="vd-aib-banner-text">{{ errorMsg() }}</span>
            <button
              type="button"
              class="vd-aib-banner-retry"
              (click)="retry()"
            >
              Réessayer
            </button>
          </div>
        }
      </div>

      <!-- Confirmation d'action sensible (mode bras droit) -->
      @if (isRightHand && pendingConfirm(); as pc) {
        <div class="vd-aib-confirm" role="alertdialog" aria-live="polite">
          <div class="vd-aib-confirm-text">
            <span class="material-symbols-outlined">help</span>
            <span>{{ pc.summary || 'Confirmer cette action sensible ?' }}</span>
          </div>
          <div class="vd-aib-confirm-actions">
            <button
              type="button"
              class="vd-aib-confirm-ok"
              (click)="confirmPending()"
              [disabled]="confirming()"
            >
              <span class="material-symbols-outlined">check</span> Confirmer
            </button>
            <button
              type="button"
              class="vd-aib-confirm-cancel"
              (click)="cancelPending()"
              [disabled]="confirming()"
            >
              <span class="material-symbols-outlined">close</span> Annuler
            </button>
          </div>
        </div>
      }

      <!-- Footer composer -->
      <footer class="vd-aib-footer">
        <div class="vd-aib-input-wrap">
          <textarea
            #inputArea
            class="vd-aib-input"
            rows="1"
            placeholder="Posez votre question… (/ticket pour le support)"
            [value]="draftMessage()"
            (input)="onInputChange($any($event.target).value)"
            (keydown)="onInputKeydown($event)"
            [disabled]="streaming() || thinking()"
            aria-label="Message à envoyer à l'assistant"
            maxlength="8000"
          ></textarea>
          <button
            type="button"
            class="vd-aib-send"
            (click)="send()"
            [disabled]="!canSend()"
            [attr.aria-label]="canSend() ? 'Envoyer le message' : 'Saisir un message'"
            title="Envoyer (Entrée)"
          >
            @if (streaming() || thinking()) {
              <span class="material-icons-outlined">pause</span>
            } @else {
              <span class="material-icons-outlined">send</span>
            }
          </button>
        </div>
        <div class="vd-aib-meta">
          <span class="vd-aib-meta-model">
            <span class="material-icons-outlined" aria-hidden="true">smart_toy</span>
            {{ isRightHand ? headerTitle : modelLabel() }}
          </span>
          @if (dailyLeft() != null) {
            <span class="vd-aib-meta-quota" [class.is-low]="(dailyLeft() || 0) < 20">
              {{ dailyLeft() }} / {{ config()?.daily_message_cap }} restants aujourd'hui
            </span>
          }
        </div>
      </footer>
    </section>
  }

  <!-- ─────────────── History side-drawer ─────────────── -->
  @if (open() && historyOpen()) {
    <div
      class="vd-aib-history-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Historique des conversations"
      (click)="closeHistory()"
    >
      <aside class="vd-aib-history" (click)="$event.stopPropagation()">
        <header class="vd-aib-history-header">
          <div>
            <h3>Historique</h3>
            <p>{{ threads().length }} conversation(s)</p>
          </div>
          <button
            type="button"
            class="vd-aib-icon-btn"
            (click)="closeHistory()"
            aria-label="Fermer l'historique"
          >
            <span class="material-icons-outlined">close</span>
          </button>
        </header>

        <div class="vd-aib-history-body">
          @if (threads().length === 0) {
            <div class="vd-aib-history-empty">
              <span class="material-icons-outlined" aria-hidden="true">forum</span>
              <p>Aucune conversation pour l'instant.</p>
              <button
                type="button"
                class="vd-aib-history-new"
                (click)="closeHistory(); newConversation()"
              >
                Commencer une conversation
              </button>
            </div>
          }
          @for (t of threads(); track trackByThread($index, t)) {
            <article
              class="vd-aib-thread"
              [class.is-active]="t.id === currentThreadId()"
              (click)="selectThread(t)"
              role="button"
              tabindex="0"
              (keydown.enter)="selectThread(t)"
            >
              <div class="vd-aib-thread-main">
                <span class="vd-aib-thread-title">
                  {{ t.title || 'Sans titre' }}
                </span>
                <span class="vd-aib-thread-sub">
                  {{ relativeTime(t.last_message_at || t.updated_at) }}
                  @if (t.message_count) {
                    · {{ t.message_count }} message(s)
                  }
                </span>
              </div>
              <div class="vd-aib-thread-actions">
                <button
                  type="button"
                  class="vd-aib-icon-btn sm"
                  (click)="archiveThread(t, $event)"
                  aria-label="Archiver"
                  title="Archiver"
                >
                  <span class="material-icons-outlined">archive</span>
                </button>
                <button
                  type="button"
                  class="vd-aib-icon-btn sm danger"
                  (click)="deleteThread(t, $event)"
                  aria-label="Supprimer"
                  title="Supprimer"
                >
                  <span class="material-icons-outlined">delete</span>
                </button>
              </div>
            </article>
          }
        </div>
      </aside>
    </div>
  }
}
